import { BILLING_CYCLE_LABELS } from "@/config/stripePlans";
import {
  plans,
  addOns,
  computeTotal,
  clientUsesBookings,
  isEarlySupporter,
  EARLY_SUPPORTER_GRANTS,
} from "../pricing";
import type { StepProps } from "../types";

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-4 py-2 border-b last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-right">{value || "—"}</span>
  </div>
);

const ReviewStep = ({ data }: StepProps) => {
  const plan = plans[data.selectedPlan];
  const earlySupporter = isEarlySupporter(data);
  const selectedAddOnNames = earlySupporter
    ? EARLY_SUPPORTER_GRANTS.filter((g) => data.earlySupporterGrants.includes(g.key))
        .map((g) => `${g.name} (free)`)
        .join(", ")
    : data.selectedAddOns
        .map((k) => addOns[k]?.name)
        .filter(Boolean)
        .join(", ");
  const total = computeTotal(data);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-heading font-semibold">Review</h2>
        <p className="text-sm text-muted-foreground">
          Confirm everything with the client before generating their payment
          link.
        </p>
      </div>

      <div className="rounded-xl border p-4">
        <h3 className="font-heading font-semibold mb-1">Account</h3>
        <Row label="Client email" value={data.clientEmail} />
        <Row
          label="Account status"
          value={data.accountCreated ? "Created" : "Not created"}
        />
      </div>

      <div className="rounded-xl border p-4">
        <h3 className="font-heading font-semibold mb-1">Business</h3>
        <Row label="Business name" value={data.businessName} />
        <Row label="Contact" value={data.contactName} />
        <Row label="Phone" value={data.phone} />
        <Row label="Category" value={data.category} />
        <Row label="Address" value={data.businessAddress} />
      </div>

      <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
        <h3 className="font-heading font-semibold mb-1">Plan</h3>
        <Row label="Plan" value={plan?.name ?? data.selectedPlan} />
        <Row label="Billing" value={BILLING_CYCLE_LABELS[data.billingCycle]} />
        <Row label="Add-ons" value={selectedAddOnNames} />
        <Row
          label="AI mode"
          value={clientUsesBookings(data) ? "Bookings (AI books & takes payment)" : "AI-assistant-only (answer + connectors)"}
        />
        <div className="flex justify-between gap-4 pt-3 mt-1">
          <span className="font-medium">Total</span>
          <span className="text-lg font-heading font-bold">
            ${total}/{data.billingCycle === "monthly" ? "mo" : `mo (billed ${data.billingCycle})`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;
